import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Tab,
  Typography,
} from '@mui/material';
import { Assignment, CloudDone, Clear } from '@mui/icons-material';
import { TabList, TabContext, TabPanel } from '@mui/lab';
import { Overview } from './Overview';
import { Requirements } from './Requirements';
import { useWorklistItem } from './hooks';
import { History } from 'components/History/';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { AssignModal } from './AssignModal';
import { t } from 'i18next';
import { useState } from 'react';
import { CustomModal } from '@components';
import { theme } from '@store';
import { Attempts } from './Attempts';
import { CommentsTab, ImproveCareNotesModal } from './components';
import { FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION } from '@common/types/constants.ts';
import { Documents } from './Documents.tsx';

interface WorklistItemProps {
  open: boolean;
  handleClose: () => void;
  itemId: string;
  openedFromPatient?: boolean;
}

export const WorklistItem = ({
  open,
  handleClose,
  itemId,
  openedFromPatient = false,
}: WorklistItemProps) => {
  const {
    handleChangeTab,
    tabIndex,
    task,
    taskHistory,
    historyPage,
    setHistoryPage,
    isCompleteOpen,
    handleOpenComplete,
    handleCloseComplete,
    confirmComplete,
    handleTriggerCloseDrawer,
    isFormDisabled,
    lockedBy,
    isSavingTask,
    setIsSavingTask,
    handleUnlockTaskAction,
    isUnlockTaskModalOpen,
    handleCloseUnlockModal,
    handleApproveTaskUnlock,
    requesterName,
    isUnlockInProgress,
    isUnlockDisabled,
    isTaskCompleting,
    historyTabIndex,
    setIsFormClean,
    triggerTabChange,
    setTriggerTabChange,
    setNewTabIndex,
    isFormClean,
    triggerCloseDrawer,
    setTriggerCloseDrawer,
    closeDrawer,
    isImproveCareNotesModalOpen,
    handleCloseImproveCareNotesModal,
    usiAiSuggestionOnComplete,
    keepOriginalOnComplete,
    aiGeneratedCareNotes,
    isInsufficientContext,
    originalCareNotes,
    isTaskLoading,
  } = useWorklistItem({
    id: itemId,
    handleClose,
    openedFromPatient,
  });

  const [isFormValid, setIsFormValid] = useState(true);

  const isCompleteDisabled =
    isTaskLoading ||
    isSavingTask ||
    isTaskCompleting ||
    !task?.has_write_permission ||
    !task?.can_be_completed ||
    isFormDisabled ||
    (!isFormValid &&
      (task?.outreach_attempts?.length ?? 0) <
        FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION);

  return (
    <Drawer
      ModalProps={{ onClose: handleTriggerCloseDrawer }}
      anchor='right'
      open={open}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: '600px',
          height: '100%',
          padding: '32px 24px 54px 24px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        }}
      >
        {task ? (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                padding: '0',
                pb: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Assignment
                  sx={{ width: '56px', height: '100%' }}
                  color='primary'
                />
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      fontSize: '20px',
                      fontWeight: '500',
                    }}
                  >
                    {task.task}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: theme => theme.palette.semiBlack.main }}
                  >
                    {task.category}
                  </Typography>
                </Box>
              </Box>

              <Box
                onClick={handleTriggerCloseDrawer}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  ':hover': {
                    cursor: 'pointer',
                    bgcolor: theme => theme.palette.grey[50],
                    borderRadius: '6px',
                  },
                  height: '30px',
                  width: '30px',
                }}
              >
                <Clear />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', width: '100%', gap: '0.5rem', pb: 1 }}>
              <AssignModal
                task_id={task.id}
                disabled={isFormDisabled || !task.has_write_permission}
              />
              <Button
                variant='contained'
                color={'secondary'}
                sx={{
                  width: '100%',
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isCompleteDisabled}
                onClick={handleOpenComplete}
              >
                COMPLETE
              </Button>
            </Box>
            <TabContext value={tabIndex}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 2,
                  '& .MuiTabs-scrollButtons.Mui-disabled': {
                    opacity: 0.3,
                  },
                }}
              >
                <TabList onChange={handleChangeTab} variant='scrollable'>
                  <Tab label='OVERVIEW' disabled={isSavingTask} value='1' />
                  <Tab label='REQUIREMENTS' disabled={isSavingTask} value='2' />
                  <Tab
                    label={`COMMENTS ${task.comments.length > 0 ? '(' + task.comments.length + ')' : ''}`}
                    disabled={isSavingTask}
                    value='3'
                  />
                  {task.outreach_task && (
                    <Tab label='ATTEMPTS' disabled={isSavingTask} value='4' />
                  )}
                  <Tab
                    label={`DOCUMENTS ${task.documents.length > 0 ? '(' + task.documents.length + ')' : ''}`}
                    disabled={isSavingTask}
                    value='5'
                  />
                  <Tab
                    label='HISTORY'
                    disabled={isSavingTask}
                    value={historyTabIndex}
                  />
                </TabList>
              </Box>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  overflowY: 'auto',
                }}
                value='1'
              >
                <Overview
                  task={task}
                  setIsSavingTask={setIsSavingTask}
                  isFormDisabled={isFormDisabled}
                  lockedBy={lockedBy}
                  handleUnlockLockedTask={handleUnlockTaskAction}
                  isUnlockInProgress={isUnlockInProgress}
                  isUnlockDisabled={isUnlockDisabled}
                />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  overflowY: 'auto',
                }}
                value='2'
              >
                <Requirements
                  task={task}
                  isFormDisabled={isFormDisabled}
                  lockedBy={lockedBy}
                  setIsSavingTask={setIsSavingTask}
                  isSavingTask={isSavingTask}
                  setFormValidity={setIsFormValid}
                  handleUnlockLockedTask={handleUnlockTaskAction}
                  isUnlockInProgress={isUnlockInProgress}
                  isUnlockDisabled={isUnlockDisabled}
                />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  overflowY: 'scroll',
                }}
                value='3'
              >
                <CommentsTab
                  commentsList={task.comments}
                  task_id={task.id}
                  setIsFormClean={setIsFormClean}
                  triggerTabChange={triggerTabChange}
                  setTriggerTabChange={setTriggerTabChange}
                  triggerCloseDrawer={triggerCloseDrawer}
                  setTriggerCloseDrawer={setTriggerCloseDrawer}
                  tabIndex={tabIndex}
                  setNewTabIndex={setNewTabIndex}
                  isFormClean={isFormClean}
                  closeDrawer={closeDrawer}
                  isFormDisabled={isFormDisabled}
                  lockedBy={lockedBy}
                  handleUnlockLockedTask={handleUnlockTaskAction}
                  isUnlockInProgress={isUnlockInProgress}
                  isUnlockDisabled={isUnlockDisabled}
                />
              </TabPanel>
              {task.outreach_task && (
                <TabPanel
                  sx={{
                    padding: '0 !important',
                    overflowY: 'auto',
                  }}
                  value='4'
                >
                  <Attempts task={task} />
                </TabPanel>
              )}
              <TabPanel
                sx={{
                  padding: '0 !important',
                }}
                value='5'
              >
                <Documents
                  disabled={isFormDisabled || !task.has_write_permission}
                  task={task}
                />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  overflowY: 'scroll',
                }}
                value={historyTabIndex}
              >
                {taskHistory && (
                  <History
                    page={historyPage}
                    changePage={setHistoryPage}
                    history={taskHistory.history}
                    count={taskHistory.count}
                  />
                )}
              </TabPanel>
            </TabContext>

            <Box
              sx={{
                position: 'fixed',
                bottom: '16px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isSavingTask ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CircularProgress
                    size='24px'
                    sx={{ color: 'rgba(0, 0, 0, 0.12)' }}
                  />
                  <Typography sx={{ color: 'text.disabled' }}>
                    {t('worklist.worklistItem.isSaving')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CloudDone
                    sx={{ height: '24px', width: '32px' }}
                    color='disabled'
                  />
                  <Typography sx={{ color: 'text.disabled' }}>
                    {t('worklist.worklistItem.saved')}
                  </Typography>
                </Box>
              )}
            </Box>

            <ConfirmationModal
              title={t('worklist.worklistItem.completeTaskModal.title')}
              message={t('worklist.worklistItem.completeTaskModal.message')}
              confirmText={t(
                `worklist.worklistItem.completeTaskModal.${isTaskCompleting ? 'completing' : 'complete'}`,
              )}
              onConfirm={confirmComplete}
              onClose={handleCloseComplete}
              isOpen={isCompleteOpen}
              disableCloseOnOutsideClick={isTaskCompleting}
              isLoading={isTaskCompleting}
            />
          </>
        ) : (
          <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
      <CustomModal
        isOpen={isUnlockTaskModalOpen}
        onClose={handleCloseUnlockModal}
        title={t('worklist.worklistItem.unlockModal.title')}
      >
        <Box>
          <Typography variant='subtitle1'>
            {t('worklist.worklistItem.unlockModal.message', {
              name: requesterName,
            })}
          </Typography>

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, py: 3 }}
          >
            <Button onClick={handleCloseUnlockModal}>
              {t('worklist.worklistItem.unlockModal.deny')}
            </Button>
            <Button
              onClick={handleApproveTaskUnlock}
              variant='contained'
              color='secondary'
              sx={{ color: theme.palette.common.white }}
            >
              {t('worklist.worklistItem.unlockModal.approve')}
            </Button>
          </Box>
        </Box>
      </CustomModal>
      {aiGeneratedCareNotes && (
        <ImproveCareNotesModal
          isOpen={isImproveCareNotesModalOpen}
          onClose={handleCloseImproveCareNotesModal}
          originalCareNotes={originalCareNotes}
          onUseIt={usiAiSuggestionOnComplete}
          onKeepOriginal={keepOriginalOnComplete}
          aiSuggestedText={aiGeneratedCareNotes}
          isLoading={isTaskCompleting}
          isInsufficientContext={isInsufficientContext}
        />
      )}
    </Drawer>
  );
};
